'use client';

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { isUnattributedOutsideInteraction } from '@/components/attributes';
import { useAddInventoryDialog } from '../../hooks/useAddInventoryDialog';
import { TypeSelectionView } from './TypeSelectionView';
import { InventoryFormView } from './InventoryFormView';
import {
  UnsavedChangesDialog,
  useUnsavedChangesGuard
} from '@/components/shared/unsaved-changes-guard';
import { MATTER_ESTATE_ADMIN_KEYS } from '@/lib/hooks/use-matter-estate-admin-data';
import type { OverviewData } from '@/lib/data/matter-overview-data';

interface AddInventoryDialogProps {
  /** Feeds `SheetTrigger asChild`, whose Slot throws on anything but a single element, so this is `ReactElement` rather than `ReactNode` (VALE-1814; see `__tests__/radix-aschild-slot-registry.test.ts`). */
  children?: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onInventoryAdded?: () => void;
  matterId?: string;
  initialInventoryType?: string; // Auto-select this type and skip type selection
  category?: 'asset' | 'liability'; // Filter types by category
}

export function AddInventoryDialog({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onInventoryAdded,
  matterId,
  initialInventoryType,
  category
}: AddInventoryDialogProps) {
  // Use controlled state if provided, otherwise manage internally
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const {
    // State
    view,
    loading,
    submitting,
    inventoryTypes,
    selectedType,
    formData,
    dynamicFormData,

    // Actions
    loadDialogData,
    handleTypeSelect,
    handleSubmit,
    handleBack,
    resetDialog
  } = useAddInventoryDialog(
    matterId,
    onInventoryAdded,
    initialInventoryType,
    category
  );

  // Get date of death from the matter query cache (already fetched by parent page)
  const queryClient = useQueryClient();
  const matterIdNum = matterId ? parseInt(matterId, 10) : 0;
  const cachedData = queryClient.getQueryData<OverviewData>(
    MATTER_ESTATE_ADMIN_KEYS.overview(matterIdNum)
  );
  const dateOfDeath = cachedData?.matter?.decedentDateOfDeath;

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      loadDialogData();
    }
  }, [open, loadDialogData]);

  const handleClose = (newOpen: boolean = false) => {
    setOpen(newOpen);
    // Reset state after animation when closing
    if (!newOpen) {
      setTimeout(() => {
        resetDialog();
      }, 200);
    }
  };

  /**
   * **Warn before a dismiss throws away a part-filled new item** (VALE-164).
   *
   * The add dialog is the one surface in this rollout that still *buffers*:
   * every row commits into local state and nothing reaches the server until
   * Save, so closing it genuinely loses work — a whole part-filled item, not one
   * field.
   *
   * **The commit-on-blur drawers guard too, for a narrower reason** (VALE-349,
   * correcting the note that used to stand here): a field held open by a
   * refusal, and a save still in flight past the indicator's threshold, are both
   * state the commit path could not store. They reach the same shared helper
   * through `AttributeUnsavedStateProvider`. So the helper was never going to be
   * dead code, and deleting it as "now-dead" would reintroduce the defect
   * VALE-164 filed here as well as the ones VALE-349 fixed there.
   *
   * `resetKey` is the chosen type: this component hosts the `Sheet` and so is
   * never remounted between opens, while `resetDialog` clears `selectedType`
   * shortly after a close — so keying on it is what makes each fresh open, and
   * each Change Type, start clean.
   */
  const unsavedChanges = useUnsavedChangesGuard({
    enabled: true,
    resetKey: selectedType?.slug ?? null,
    open,
    onOpenChange: handleClose
  });

  const handleFormSubmit = async (dynamicData: Record<string, unknown>) => {
    await handleSubmit(dynamicData);
    if (!submitting) {
      handleClose();
    }
  };

  const sheetContent = (
    <SheetContent
      side="right"
      className="flex w-full flex-col overflow-hidden p-0 sm:w-[57%] sm:max-w-none"
      onInteractOutside={(e) => {
        // Prevent closing on outside click during submission
        if (submitting) {
          e.preventDefault();
          return;
        }
        /*
          **Refuse an outside-interaction verdict reached with no attribution**
          (VALE-1677, extending VALE-1652 off `ai-import-dialog-content.tsx`).
          The form view renders the attribute treatment's composite popovers
          (`inventoryBlocks`, the same builders `InventoryAttributeDrawer`
          uses), whose capture-phase commit can unmount the pointerdown target
          before Radix's dismissable layer looks at it — see
          `isUnattributedOutsideInteraction`'s own doc for the mechanism.
        */
        if (isUnattributedOutsideInteraction(e)) e.preventDefault();
      }}
    >
      {/* Loading overlay while creating inventory item */}
      {submitting && (
        <div className="bg-background/80 absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-muted-foreground text-sm">
              Creating inventory item...
            </p>
          </div>
        </div>
      )}

      <SheetHeader className="flex-shrink-0 space-y-4 px-6 pt-8">
        <SheetTitle>
          {view === 'type-selection'
            ? category === 'liability'
              ? 'Add Liability'
              : 'Add Inventory Item'
            : `Add ${selectedType?.name}`}
        </SheetTitle>
      </SheetHeader>

      {/* **No `pb-6` here — each view owns its own bottom spacing** (VALE-285).
          The form view's footer is `sticky bottom-0`, and a sticky offset
          resolves against this scrollport's *content* box: any padding here
          holds the footer that far up and leaves a strip of record scrolling
          visibly beneath it. Cancelling that with a matching negative offset on
          the footer would just restate this padding in a second place, ready to
          drift. */}
      <div className="flex-1 overflow-y-auto px-6">
        {view === 'type-selection' ? (
          <div className="pb-6">
            <TypeSelectionView
              inventoryTypes={inventoryTypes}
              loading={loading}
              onTypeSelect={handleTypeSelect}
              category={category}
            />
          </div>
        ) : view === 'form' && selectedType ? (
          <InventoryFormView
            selectedType={selectedType}
            formData={formData}
            dynamicFormData={dynamicFormData}
            submitting={submitting}
            onSubmit={handleFormSubmit}
            onBack={handleBack}
            /* Through the guard, not straight to `handleClose` — Cancel is a
               dismiss like any other, and on a buffered surface it is the one
               most likely to be hit by accident. */
            onCancel={() => unsavedChanges.handleOpenChange(false)}
            dateOfDeath={dateOfDeath}
            matterId={matterId ? parseInt(matterId, 10) : undefined}
            onDirtyChange={unsavedChanges.setIsDirty}
          />
        ) : null}
      </div>
    </SheetContent>
  );

  /* Rendered outside the `Sheet` in both branches: it has to survive the sheet
     being dismissed, which is the very thing it is asking about. */
  const unsavedChangesDialog = (
    <UnsavedChangesDialog
      open={unsavedChanges.showUnsavedDialog}
      onOpenChange={unsavedChanges.setShowUnsavedDialog}
      onDiscard={unsavedChanges.handleDiscardChanges}
      itemLabel="item"
    />
  );

  // If children are provided, render with trigger
  if (children) {
    return (
      <>
        <Sheet open={open} onOpenChange={unsavedChanges.handleOpenChange}>
          <SheetTrigger asChild>{children}</SheetTrigger>
          {sheetContent}
        </Sheet>
        {unsavedChangesDialog}
      </>
    );
  }

  // Otherwise just render the sheet (controlled externally)
  return (
    <>
      <Sheet open={open} onOpenChange={unsavedChanges.handleOpenChange}>
        {sheetContent}
      </Sheet>
      {unsavedChangesDialog}
    </>
  );
}
